import { Typography, Stack, Grid2 as Grid, Chip, Tabs, Tab } from '@mui/material'
import ProfileInformation from './details/components/ProfileInformation'
import StudyAssignments from './details/components/StudyAssignments'
import Settings from './details/components/Settings'
import { useCompaniesMetadata, useUpdateUser, useUserDetails } from './hooks'
import { useParams } from 'react-router-dom'
import { UserCreateSchema } from '@common/config/api/client'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { useMemo, useState, useEffect, useCallback } from 'react'
import EnhancedTabPanel from '@common/components/Tab/EnhancedTabPanel'
import { UserManagementPermission } from '@auth/permissionsEnum'
import { useGlobalPermissions } from '@auth/hooks/useGlobalPermissions'
import { Breadcrumb } from '@common/components/Breadcrumb'
import { USER_MANAGEMENT_BREADCRUMB_ITEMS } from './constants'

const formatDate = (date: string) => {
  return new Date(date)
    .toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
    .replace(/\./g, '/')
}

export default function UserDetails() {
  const { userId } = useParams()
  const { data: user, isPending: isUserDetailsPending } = useUserDetails({
    userId: userId ?? '',
  })
  const { hasAnyPermission } = useGlobalPermissions()

  const updateUserMutation = useUpdateUser()
  const { data: companiesMetadata, isPending: isPendingCompaniesMetadata } = useCompaniesMetadata()
  const isLoading = isUserDetailsPending || isPendingCompaniesMetadata

  const isUserActive = useMemo(() => user?.user_status === 'ACTIVE', [user])
  const [activeTab, setActiveTab] = useState(0)

  // Use useCallback for handleUpdateUser to prevent unnecessary rerenders
  const handleUpdateUser = useCallback(
    async (userData: UserCreateSchema) => {
      if (!userId || !user) return
      try {
        await updateUserMutation.mutateAsync({
          userId: user.user_id as string,
          userData,
        })
        toast(<Toast message="User updated successfully" variant="success" />)
      } catch (error) {
        console.error('Failed to update user:', error)
        toast(<Toast message="Failed to update user. Please try again." variant="error" />)
      }
    },
    [userId, user, updateUserMutation],
  )

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }, [])

  // Reset active tab when user changes
  useEffect(() => {
    setActiveTab(0)
  }, [userId])

  // Define tabs with useMemo to properly handle dependencies
  const tabs = useMemo(
    () => [
      {
        label: 'Profile information',
        content: (
          <ProfileInformation user={user} companiesMetadata={companiesMetadata} onUpdateUser={handleUpdateUser} />
        ),
        permissions: [UserManagementPermission.ALL, UserManagementPermission.VIEW_USER],
      },
      {
        label: 'Study Assignments',
        content: <StudyAssignments user={user} />,
        permissions: [UserManagementPermission.ALL, UserManagementPermission.LIST_STUDY_ASSIGNMENTS],
      },
      {
        label: 'Settings',
        content: <Settings user={user} />,
        permissions: [UserManagementPermission.ALL, UserManagementPermission.UPDATE_USER_SETTINGS],
      },
    ],
    [user, companiesMetadata, handleUpdateUser],
  )

  // Filter tabs based on permissions
  const filteredTabs = useMemo(
    () => tabs.filter((tab) => !tab.permissions || hasAnyPermission(tab.permissions)),
    [tabs, hasAnyPermission],
  )

  // Ensure activeTab is valid after filtering
  useEffect(() => {
    if (filteredTabs.length > 0 && activeTab >= filteredTabs.length) {
      setActiveTab(0)
    }
  }, [filteredTabs, activeTab])

  if (isLoading) {
    return <div>Loading...</div>
  }

  // If no tabs are available due to permissions, show a message
  if (filteredTabs.length === 0) {
    return (
      <Typography variant="h6" align="center" sx={{ mt: 4 }}>
        You don't have permission to view any user details.
      </Typography>
    )
  }

  return (
    <>
      <Breadcrumb items={USER_MANAGEMENT_BREADCRUMB_ITEMS} />
      <br />
      <Grid container columnSpacing={SPACING.spacingXl}>
        <Grid size={SIZING.gridSizeXl}>
          <Stack spacing={SPACING.spacingLg}>
            <Stack>
              <Stack direction="row" spacing={SPACING.spacingXl} alignItems="center">
                <Typography variant="h4">{user?.user_name}</Typography>
                <Chip
                  label={isUserActive ? 'Active' : 'Deactivated'}
                  size="small"
                  color={isUserActive ? 'success-alt' : 'error-alt'}
                  sx={{ borderRadius: 100 }}
                />
              </Stack>
              <Stack direction="row" spacing={SPACING.minSpacing} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  User created: {user?.created_at && formatDate(user.created_at)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  -{' '}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created by: {user?.created_by ?? '-'}
                </Typography>
              </Stack>
            </Stack>
            <Tabs value={activeTab} onChange={handleTabChange}>
              {filteredTabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
            {filteredTabs.map((tab, index) => (
              <EnhancedTabPanel value={activeTab} index={index} key={index}>
                {tab.content}
              </EnhancedTabPanel>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </>
  )
}
